import {Back, Linear, gsap} from 'gsap/gsap-core';
import * as THREE from 'three';

import {GLTFGroup} from '../lib/GLTFGroup';

import {BallsGLTF} from './BallsGLTF';
import {ModelViewer} from '../lib/ModelViewer';

export class MachineGLTF extends GLTFGroup {
    private cameraMove = false;
    private ballsGLTF: BallsGLTF[];
    private frontGlassMaterial: THREE.MeshPhysicalMaterial;
    private sideGlassMaterial: THREE.MeshPhysicalMaterial;
    private params: any;

    constructor(viewer: ModelViewer, gltf: GLTFGroup, name: string, ballsGLTF: BallsGLTF[], params = {}) {
        super(viewer, gltf, name);
        this.ballsGLTF = ballsGLTF;
        this.params = {
            cameraAnimationStartTime: 3,
            cameraAnimationDuration: 7,
            cameraZOffset: 1.5,
            cameraYOffset: 0.41,
            topBallScale: 3,
            topBallAnimationDuration: 1,
            scoreUrl: null,
            ballAnimationDuration: 0.5,
            gAnimationRowElements: 6,
            gAnimationRowStep: 0.09,
            gMaxPerRow: 12,
            ...params,
        };

        this.frontGlassMaterial = new THREE.MeshPhysicalMaterial({
            color: new THREE.Color(1, 1, 1),
            roughness: 0,
            transmission: 1,
            ior: 1.7,
            specularIntensity: 1,
            clearcoat: 1,
            side: THREE.DoubleSide,
        });

        this.sideGlassMaterial = new THREE.MeshPhysicalMaterial({
            color: new THREE.Color(0, 0, 0),
            roughness: 0,
            transmission: 1,
            ior: 1.7,
            specularIntensity: 1,
            clearcoat: 1,
            side: THREE.DoubleSide,
        });
    }

    static ballsFromAssets(viewer: any, assets: any) {
        const balls = [];
        assets.textures.forEach((textureName, i) => {
            const b = new BallsGLTF(
                viewer,
                viewer.getGLTF(assets.balls[i].objectName),
                assets.textures[i].name,
                assets.textures[i].type,
                assets.textures[i].color
            );

            viewer.addModel(b);
            balls.push(b);
        });

        return balls;
    }

    static loadAssetsFromAssets(viewer, assets, ballsGLTF) {
        assets.textures = [];
        assets.balls = [];

        ballsGLTF.forEach((ball, i) => {
            const textureName = `balls_texture_${i}`;
            const ballObjectName = `balls_object_${i}`;

            switch (ballsGLTF[i].texture) {
                case BallsGLTF.whiteBlack:
                    viewer.loadSRGBTexture(textureName, 'draw-animation/textures/balls/white_black_balls_diff_1k.jpg');
                    break;
                case BallsGLTF.blackWhite:
                default:
                    viewer.loadSRGBTexture(textureName, 'draw-animation/textures/balls/black_white_balls_diff_1k.jpg');
                    break;
            }

            viewer.loadGLTF(ballObjectName, ballsGLTF[i].balls);

            assets.textures.push({
                name: textureName,
                type: ballsGLTF[i].texture,
                color: ballsGLTF[i].color,
            });

            assets.balls.push({
                objectName: ballObjectName,
                positions: ballsGLTF[i].positions,
                suffix: ballsGLTF[i].suffix,
                result: ballsGLTF[i].result,
                maxPerRow: ballsGLTF[i].maxPerRow,
            });
        });

        // GLTF
        viewer.loadGLTF(assets.machine, viewer.getPathToAssets() + assets.file);
    }

    sceneUpdate(scene) {
        scene.position.copy(this.viewer.scene.getObjectByName('MachinePosition').position);
    }

    meshUpdate(model) {
        model.material.side = THREE.FrontSide;
        model.receiveShadow = true;
        model.castShadow = true;

        if (/TubeEnter[0-9]{2}/.test(model.name)) {
            model.castShadow = false;
        }

        if (/Detail.*/.test(model.name)) {
            model.castShadow = false;
        }

        // metalness
        model.material.metalness = 0.01;
        if (model.material.name === 'MachineDetails') {
            model.material.metalness = 0.8;
        }

        const envMap = this.viewer.getEnvMap();
        if (envMap) {
            model.material.envMap = envMap;
            model.material.envMapIntensity = this.viewer.getEnvMapIntensity();
        }

        if (model.material.name === 'Background') {
            model.material = new THREE.MeshStandardMaterial({
                color: '#000000',
                opacity: 0.9,
                transparent: 1,
            });
        }

        if (model.material.name === 'Glass') {
            model.material = this.sideGlassMaterial;
            model.receiveShadow = true;
            model.castShadow = false;
        }

        if (model.material.name === 'GlassFront') {
            model.material = this.frontGlassMaterial;
            model.receiveShadow = true;
            model.castShadow = false;
        }
    }

    // eslint-disable-next-line class-methods-use-this
    animationUpdate(animation) {
    }

    animate(delta: number) {
        super.animate(delta);

        const readyTime = this.viewer.getReadyTime();
        if (
            !this.cameraMove &&
            readyTime > 0 &&
            this.viewer.getCurrentTime() > this.params.cameraAnimationStartTime + readyTime
        ) {
            this.cameraMove = true;

            const cameraView = this.gltf.scene.getObjectByName('CameraView').position;

            const timeline = gsap.timeline({
                repeat: 0,
            });
            timeline.to(this.viewer.camera.position, {
                x: this.gltf.scene.position.x,
                z: this.gltf.scene.position.z + cameraView.z + this.params.cameraZOffset,
                y: this.gltf.scene.position.y + cameraView.y + this.params.cameraYOffset,
                duration: this.params.cameraAnimationDuration,
                onComplete: () => {
                    this.mixer.stopAllAction();
                    this.ballsGLTF.forEach((balls) => {
                        balls.mixer.stopAllAction();
                    });
                    this.ballAnimation();
                },
            });
        }
    }

    // eslint-disable-next-line class-methods-use-this
    ballAnimation() {
    }

    ballAnimationFromAssets(assetBalls) {
        let ballPosition = 1;
        let k = 0;

        for (let i = 0; i < assetBalls.length; i++) {
            const balls = assetBalls[i];

            if (balls.result.length === balls.positions) {
                for (let j = 0; j < balls.positions; j++) {
                    const ballPositionName = `BallPosition${this.getPositionNumber(ballPosition++)}`;
                    this.animateBall(k++, this.ballsGLTF[i].getScene(), balls.result[j], ballPositionName, balls.suffix);
                }
            }
        }
    }

    getBallName(name: string, number: number, suffix: string) {
        return name + this.getBallNumber(number) + suffix;
    }

    // eslint-disable-next-line class-methods-use-this
    getBallNumber(number: number) {
        return `00${number}`.substr(-3);
    }

    // eslint-disable-next-line class-methods-use-this
    getPositionNumber(number: number) {
        return `00${number}`.substr(-2);
    }

    static getNumbers(length: number, from: number, to: number) {
        const numbers = [];
        let nextNumber;
        do {
            nextNumber = Math.floor(Math.random() * to) + from;
            if (!numbers.includes(nextNumber)) {
                numbers.push(nextNumber);
            }
        } while (numbers.length < length);

        return numbers;
    }

    animateBall(index, ballsGLTF, number, positionName, suffix) {
        const name = this.getBallName('Ball_', number, suffix);
        const ballPosition = this.gltf.scene.getObjectByName(positionName).position;
        const ball = ballsGLTF.getObjectByName(name);
        if (ball) {
            ball.rotation.set(-Math.PI / 2, 0, 0);
            ball.position.set(ballPosition.x, ballPosition.y - 0.3, ballPosition.z);
            // animate ball up
            gsap.to(ball.position, {
                y: ballPosition.y,
                delay: index * 0.1,
                duration: this.params.ballAnimationDuration,
                ease: Back.easeInOut.config(1),
                onComplete: () => {
                    /*
                    // clone ball and add to group
                    const topBall = ball.clone();
                    this.gltf.scene.add(topBall);
                    topBall.position.add(new THREE.Vector3(0, 0.2, 0));
                    topBall.scale.set(0, 0, 0);

                    // animate scale up
                    gsap.to(topBall.scale, {
                        x: this.params.topBallScale,
                        y: this.params.topBallScale,
                        z: this.params.topBallScale,
                        duration: this.params.topBallAnimationDuration,
                    });
                    */
                },
            });
        } else throw new Error(`No ball with name: ${name}`);
    }

    gAnimateBall(index, ballsGLTF, number, startPositionName, endPositionName, suffix) {
        const name = this.getBallName('Ball_', number, suffix);
        const startBallPosition = this.gltf.scene.getObjectByName(startPositionName).position;
        const endBallPosition = this.gltf.scene.getObjectByName(endPositionName).position;
        const ball = ballsGLTF.getObjectByName(name);

        if (ball) {
            ball.rotation.set(-Math.PI / 2, Math.PI * 4, 0);
            ball.position.set(startBallPosition.x, startBallPosition.y, startBallPosition.z);

            const timeline = gsap.timeline({
                repeat: 0,
            });
            timeline
                .to(ball.position, {
                    x: endBallPosition.x,
                    y: endBallPosition.y,
                    z: endBallPosition.z,
                    ease: Linear.easeNone,
                    duration: this.params.ballAnimationDuration,
                    onComplete: () => {
                        const scorePosition = this.gltf.scene.getObjectByName('BallScorePosition').position;
                        // clone ball and add to group
                        const topBall = ball.clone();
                        this.gltf.scene.add(topBall);

                        const x =
                            scorePosition.x +
                            (index % this.params.gAnimationRowElements) * this.params.gAnimationRowStep;
                        const y =
                            scorePosition.y -
                            (index < this.params.gAnimationRowElements ? 0 : this.params.gAnimationRowStep);
                        topBall.position.copy(new THREE.Vector3(x, y, scorePosition.z));
                        topBall.scale.set(0, 0, 0);
                        topBall.rotation.set(-Math.PI / 2, 0, 0);

                        // // animate scale up
                        // gsap.to(topBall.scale, {
                        //     x: this.params.topBallScale,
                        //     y: this.params.topBallScale,
                        //     z: this.params.topBallScale,
                        //     duration: this.params.topBallAnimationDuration,
                        // });
                    },
                })
                .to(
                    ball.rotation,
                    {
                        y: 0,
                        duration: this.params.ballAnimationDuration,
                        ease: Linear.easeNone,
                    },
                    `-=${this.params.ballAnimationDuration}`
                );
        } else throw new Error(`No ball with name: ${name}`);
    }

    gAnimateBallFromAssets(
        assetBalls: { result: number[]; positions: number; suffix: string; maxPerRow: number }[],
        timeToWait: number
    ) {
        let ballPosition = 1;
        let timeLine = 0;
        let row = 0;
        const rowLength = this.params.gMaxPerRow;

        for (let i = 0; i < assetBalls.length; i++) {
            const balls = assetBalls[i];
            const maxPerRow = Math.min(balls.maxPerRow, rowLength);

            if (balls.result.length === balls.positions) {
                for (let j = 0; j < balls.positions; j++) {
                    // eslint-disable-next-line no-loop-func
                    (() => {
                        setTimeout(() => {
                            this.gAnimateBall(
                                j,
                                this.ballsGLTF[i].getScene(),
                                balls.result[j],
                                `BallStartPosition${this.getPositionNumber(ballPosition)}`,
                                `BallPosition${this.getPositionNumber(ballPosition)}`,
                                balls.suffix
                            );
                            if (ballPosition - row * rowLength >= maxPerRow) {
                                ballPosition += rowLength - maxPerRow + 1;
                                row++;
                            } else {
                                ballPosition++;
                            }
                        }, timeLine);
                        timeLine += Math.random() * timeToWait + 100;
                    })();
                }
            }
        }
    }
}
